import { BannerStatus, Column, PromoBanner } from "src/api/types";
import format from "date-fns/format";
import { useCallback, useMemo } from "react";
import {
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { statuses } from "./filters";
import { useUpdateBannerMutation } from "src/api/services/bannersApi";
import { usePromoBanner } from "src/context/UpdateBannerProvider";
import { Archive, Edit } from "@mui/icons-material";

export const useColumns = (): Column<PromoBanner>[] => {
  const [updateBanner] = useUpdateBannerMutation();
  const { setBanner } = usePromoBanner();

  const handleUpdate = useCallback(
    (row: PromoBanner) => setBanner(row),
    [setBanner],
  );

  const handleStatusUpdate = useCallback(
    async (value: string, row: PromoBanner) => {
      const { error } = await updateBanner({
        id: row.id,
        body: { status: value as BannerStatus },
      });

      if (error) {
        alert(
          `Unable to set ${row.language.toUpperCase()} ${
            row.product.name
          } flow banner to active, please confirm that the current banner is inactive`,
        );
      }
    },
    [updateBanner],
  );

  const handleStatus = useCallback(
    (value: string, row: PromoBanner) => {
      handleStatusUpdate(value, row);
    },
    [handleStatusUpdate],
  );

  const handleArchive = useCallback(
    (row: PromoBanner) => {
      if (row.status === "active") {
        alert(
          "You cannot archive active banners. Please deactivate banner before archiving it",
        );
        return;
      }
      handleStatusUpdate("archived", row);
    },
    [handleStatusUpdate],
  );

  return useMemo(
    () => [
      {
        id: "product_id",
        label: "Product",
        minWidth: 50,
        align: "center",
        orderable: false,
        format: (banner) => banner.product.name,
        render: (banner) => <Typography>{banner.product.name}</Typography>,
      },
      {
        id: "text",
        label: "Banner text",
        minWidth: 200,
        align: "center",
        orderable: false,
        format: (banner) => banner.text,
        render: (banner) => <Typography>{banner.text}</Typography>,
      },
      {
        id: "language",
        label: "Language",
        minWidth: 50,
        align: "center",
        orderable: false,
        format: (banner) => banner.language,
        render: (banner) => <Typography>{banner.language}</Typography>,
      },
      {
        id: "created_by",
        label: "Created By",
        minWidth: 50,
        align: "center",
        orderable: false,
        format: (banner) => banner.created_by,
        render: (banner) => <Typography>{banner.created_by}</Typography>,
      },
      {
        id: "created_at",
        label: "Date Created",
        minWidth: 50,
        align: "center",
        orderable: false,
        format: (banner) =>
          format(new Date(banner.created_at || ""), "MM/dd/yyyy"),
        render: (banner) => (
          <Typography>
            {format(new Date(banner.created_at || ""), "MM/dd/yyyy")}
          </Typography>
        ),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 20,
        align: "center",
        orderable: false,
        format: (banner) => banner.status,
        render: (banner) => (
          <TextField
            select
            value={banner.status}
            size="small"
            sx={{ width: "fit-content" }}
            onChange={(e) => handleStatus(e.target.value, banner)}
          >
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        ),
      },
      {
        id: "id",
        label: "Actions",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (banner) => banner.id.toString(),
        render: (banner) => (
          <Stack direction="row" justifyContent="center">
            <Tooltip title="Archive">
              <IconButton color="error" onClick={() => handleArchive(banner)}>
                <Archive />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleUpdate(banner)} color="primary">
                <Edit />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleArchive, handleStatus, handleUpdate],
  );
};
